"use client";

import { useTranslations } from "next-intl";
import type { Locale } from "@/lib/i18n";
import type { PublicSiteNotice } from "@/lib/platform-public-settings";
import { getLocalizedField } from "@/features/courses/api";
import { SITE_NOTICE_DISMISSAL_KEY, persistSiteNoticeDismissed } from "@/lib/site-notice-dismissal";

interface PlaygroundSiteNoticeRowProps {
  notice?: PublicSiteNotice;
  locale: Locale;
}

/**
 * The admin-managed site notice (`AD-SN01`), styled for Playground's dark
 * toolbar chrome and rendered inside `PlaygroundLayout`'s existing bordered
 * notice slot next to `PlaygroundEngineNotice` — not the light global
 * `SiteNoticeBar`, which is deliberately excluded on Playground routes
 * because they're a fixed `100dvh` box (`docs/24` §10.1). Same server-
 * rendered-then-inline-script dismissal technique as `SiteNoticeBar`.
 */
export function PlaygroundSiteNoticeRow({ notice, locale }: PlaygroundSiteNoticeRowProps) {
  const t = useTranslations("common.siteNotice");
  if (!notice) return null;

  const title = getLocalizedField(notice.title_en, notice.title_bn, locale);
  const body = getLocalizedField(notice.body_en, notice.body_bn, locale);
  const linkLabel = getLocalizedField(notice.linkLabel_en, notice.linkLabel_bn, locale) || t("defaultLinkLabel");
  const rowId = `playground-site-notice-${notice.id}`;

  function handleDismiss() {
    persistSiteNoticeDismissed(notice!.id);
    const el = document.getElementById(rowId);
    if (el) el.style.display = "none";
  }

  return (
    <section
      id={rowId}
      aria-labelledby={`${rowId}-title`}
      className="flex flex-wrap items-start justify-between gap-3 text-sm text-white/85"
    >
      <div className="min-w-0 flex-1 space-y-1">
        <p id={`${rowId}-title`} className="font-semibold text-white">
          {title}
        </p>
        <p className="text-white/75">
          {body}
          {notice.linkUrl ? (
            <>
              {" "}
              <a
                href={notice.linkUrl}
                className="text-[#FFAB8F] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C]"
              >
                {linkLabel}
              </a>
            </>
          ) : null}
        </p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="inline-flex min-h-[44px] items-center rounded-lg border border-white/15 px-3 text-sm font-semibold text-white/85 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] sm:min-h-[36px]"
      >
        {t("dismiss")}
      </button>
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `(function(){try{if(window.localStorage.getItem(${JSON.stringify(
            SITE_NOTICE_DISMISSAL_KEY,
          )})===${JSON.stringify(notice.id)}){var el=document.getElementById(${JSON.stringify(
            rowId,
          )});if(el)el.style.display="none";}}catch(e){}})();`,
        }}
      />
    </section>
  );
}
